/**
 * Hook to read/write a user's saved signature, stored as a Basic resource in Medplum.
 *
 * Stored as a Basic resource with the signature JSON in an extension.
 * The Basic resource is scoped to a Practitioner via `subject`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Basic } from '@medplum/fhirtypes';
import {
  USER_SIGNATURE_CONFIG_CODE,
  USER_SIGNATURE_CONFIG_DATA_URL,
  USER_SIGNATURE_CONFIG_SYSTEM,
} from '@unify/fhir-model/constants';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';

export interface UserSignatureConfig {
  /** Partial because stored JSON is validated field by field and a surviving record may lack `name`. */
  savedSignature?: Partial<SignatureInfo>;
  preferredMode: 'type' | 'draw';
}

const DEFAULT_CONFIG: UserSignatureConfig = { preferredMode: 'type' };

const SIGNATURE_STRING_FIELDS = [
  'name',
  'title',
  'license',
  'signedAt',
  'ipAddress',
  'signatureData',
  'signatureFormat',
  'typedText',
] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Parses stored signature JSON; anything malformed or mis-shaped yields the default config. */
export function parseUserSignatureConfig(raw: string | undefined): UserSignatureConfig {
  if (!raw) return DEFAULT_CONFIG;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_CONFIG;
  }
  if (!isPlainObject(parsed)) return DEFAULT_CONFIG;

  const config: UserSignatureConfig = {
    preferredMode: parsed.preferredMode === 'draw' ? 'draw' : 'type',
  };
  if (isPlainObject(parsed.savedSignature)) {
    const source = parsed.savedSignature;
    const saved: Partial<SignatureInfo> = {};
    for (const field of SIGNATURE_STRING_FIELDS) {
      const value = source[field];
      if (typeof value === 'string') saved[field] = value;
    }
    if (source.signatureMode === 'type' || source.signatureMode === 'draw') {
      saved.signatureMode = source.signatureMode;
    }
    if (Object.keys(saved).length > 0) config.savedSignature = saved;
  }
  return config;
}

/** Writes are allowed only for the logged-in practitioner's own signature. */
export function canWriteSignatureConfig(
  explicitId: string | undefined,
  currentId: string | undefined,
): boolean {
  return explicitId === undefined || explicitId === currentId;
}

export type UserSignatureStatus = 'loading' | 'ready' | 'error';

export interface UseUserSignatureResult {
  config: UserSignatureConfig;
  loading: boolean;
  status: UserSignatureStatus;
  saveConfig: (config: UserSignatureConfig) => Promise<void>;
}

export function useUserSignature(explicitPractitionerId?: string): UseUserSignatureResult {
  const medplum = useMedplum();
  const { practitioner, loading: practLoading } = useCurrentUserPractitioner();
  const [config, setConfig] = useState<UserSignatureConfig>(DEFAULT_CONFIG);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [status, setStatus] = useState<UserSignatureStatus>('loading');

  const currentId = practitioner?.id;
  const targetId = explicitPractitionerId ?? currentId;
  const waitingForCurrentUser = explicitPractitionerId === undefined && practLoading;

  useEffect(() => {
    setConfig(DEFAULT_CONFIG);
    setResourceId(null);
    if (!targetId) {
      setStatus(waitingForCurrentUser ? 'loading' : 'error');
      return;
    }
    let cancelled = false;

    async function fetchConfig() {
      setStatus('loading');
      try {
        const bundle = await medplum.search('Basic', {
          code: `${USER_SIGNATURE_CONFIG_SYSTEM}|${USER_SIGNATURE_CONFIG_CODE}`,
          subject: `Practitioner/${targetId}`,
          _sort: '-_lastUpdated',
          _count: '1',
        });
        if (cancelled) return;

        const resource = bundle.entry?.[0]?.resource as Basic | undefined;
        const ext = resource?.extension?.find((e) => e.url === USER_SIGNATURE_CONFIG_DATA_URL);
        setConfig(parseUserSignatureConfig(ext?.valueString));
        setResourceId(resource?.id ?? null);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    fetchConfig();
    return () => { cancelled = true; };
  }, [medplum, targetId, waitingForCurrentUser]);

  const saveConfig = useCallback(
    async (newConfig: UserSignatureConfig): Promise<void> => {
      if (!canWriteSignatureConfig(explicitPractitionerId, currentId)) {
        throw new Error('Cannot change another user\'s signature');
      }
      if (!currentId) return;

      const resource: Basic = {
        resourceType: 'Basic',
        ...(resourceId ? { id: resourceId } : {}),
        code: {
          coding: [{ system: USER_SIGNATURE_CONFIG_SYSTEM, code: USER_SIGNATURE_CONFIG_CODE }],
        },
        subject: { reference: `Practitioner/${currentId}` },
        extension: [
          {
            url: USER_SIGNATURE_CONFIG_DATA_URL,
            valueString: JSON.stringify(newConfig),
          },
        ],
      };

      const saved: Basic = resourceId
        ? await medplum.updateResource(resource)
        : await medplum.createResource(resource);

      setResourceId(saved.id ?? null);
      setConfig(newConfig);
    },
    [medplum, explicitPractitionerId, currentId, resourceId],
  );

  return { config, loading: status === 'loading', status, saveConfig };
}
